import { createRoute } from '@tanstack/react-router';
import { appRoute } from '@/features/dashboard/_root/routes/appRoute.tsx';
import { queryClient } from '@/App.tsx';
import ChangePackageFlowPage from '@/features/dashboard/bookingChanges/pages/ChangePackageFlowPage.tsx';
import ChangePackageFlowPageSkeleton from '@/features/dashboard/bookingChanges/components/flow/ChangePackageFlowPageSkeleton.tsx';
import DetailsErrorPage from '@/features/dashboard/bookingChanges/components/DetailsErrorPage.tsx';
import {
  ChangePackageStep,
  changePackageSearchSchema,
} from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import { getBookingBillQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingBill.ts';
import { getBookingDetailsQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import {
  getOpenDraftQueryOptions,
  isCachedOpenChange,
} from '@/features/dashboard/bookingChanges/hooks/useGetOpenDraft.ts';
import { getSchedulePreviewQueryOptions } from '@/features/dashboard/bookingChanges/hooks/useGetSchedulePreview.ts';
import { getTourConsentsQueryOptions } from '@/features/dashboard/tourConsents/hooks/useGetTourConsents.ts';

/**
 * Przepływ wprowadzania paczki zmian (W1 → W2 → W3).
 *
 * Krok siedzi w `?step=`, więc jedna trasa obsługuje wszystkie trzy ekrany —
 * odświeżenie strony wraca w to samo miejsce, a wyjście z przepływu prowadzi na
 * ekran rezerwacji.
 *
 * Każdy krok czyta co innego, więc loader zależy od `?step=` przez `loaderDeps`
 * i dociąga wyłącznie to, co widoczny krok naprawdę pokaże.
 */
export const changePackageFlowRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/bookings/$bookingId/changes/$changeId',
  component: ChangePackageFlowPage,
  validateSearch: changePackageSearchSchema,
  loaderDeps: ({ search }) => ({ step: search.step }),
  loader: async ({ params, deps }) => {
    const { bookingId, changeId } = params;

    if (deps.step === ChangePackageStep.SETTLEMENT) {
      // Lista dokumentów to zgody wyjazdu, więc po `tourId` z metryczki
      // rezerwacji idzie drugie zapytanie. Przy przejściu z W1 metryczka jest
      // już w pamięci podręcznej i dociąga się tylko druga połowa.
      const [, booking] = await Promise.all([
        queryClient.ensureQueryData(getOpenDraftQueryOptions(bookingId)),
        queryClient.ensureQueryData(getBookingDetailsQueryOptions(bookingId)),
        queryClient.ensureQueryData(getBookingBillQueryOptions(bookingId)),
      ]);
      await Promise.all([
        queryClient.ensureQueryData(
          getTourConsentsQueryOptions(booking.tourId),
        ),
        // Harmonogram nic nie kosztuje w tej fali, bo i tak czekamy na zgody,
        // a dopiero teraz wiadomo, czy paczka jest jeszcze otwarta — zamkniętej
        // backend podglądu odmawia. Wejście wstecz na krok wprowadzonej paczki
        // pokazuje więc pusty stan, a nie błąd.
        isCachedOpenChange(bookingId, changeId)
          ? queryClient.ensureQueryData(
              getSchedulePreviewQueryOptions(bookingId, changeId),
            )
          : null,
      ]);
      return;
    }

    if (deps.step === ChangePackageStep.ACCEPTANCE) {
      /*
       * Podgląd W3 składa się z danych operatora, nie z publicznego odczytu
       * paczki: token rezerwacji powstaje wyłącznie w szablonie wiadomości.
       * Zgody idą po `tourId` z metryczki rezerwacji, tak samo jak w kroku W2.
       */
      const booking = await queryClient.ensureQueryData(
        getBookingDetailsQueryOptions(bookingId),
      );
      await Promise.all([
        queryClient.ensureQueryData(getBookingBillQueryOptions(bookingId)),
        queryClient.ensureQueryData(getOpenDraftQueryOptions(bookingId)),
        queryClient.ensureQueryData(
          getTourConsentsQueryOptions(booking.tourId),
        ),
      ]);
      return;
    }

    await Promise.all([
      queryClient.ensureQueryData(getBookingBillQueryOptions(bookingId)),
      queryClient.ensureQueryData(getOpenDraftQueryOptions(bookingId)),
    ]);
  },
  pendingComponent: ChangePackageFlowPageSkeleton,
  errorComponent: DetailsErrorPage,
});
